import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Tooltip, Button } from 'antd';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { InputFromUser } from '../../../components/Input/input';
import { PrakurtiCirculerLogo } from '../../../components/imagepath';
import { formatTime } from '../../../utils/helpers/dateHelpers';

/**
 * Custom hook for service slot table columns configuration
 * @param {Object} options - Configuration options
 * @param {boolean} options.canUpdate - Permission to update service slots
 * @param {Function} options.onStatusUpdate - Callback for status updates
 * @param {Function} options.onAssignDoctor - Callback for assigning doctor
 * @returns {Array} Column configuration for AdvancedTable
 */
export const useServiceSlotColumns = ({ canUpdate, onStatusUpdate, onAssignDoctor }) => {
    const location = useLocation();
    const params = queryString.parse(location.search);
    const pageSize = 10;

    // Status color mapping
    const getStatusColor = (status) => {
        const colors = {
            'Upcoming': 'blue',
            'InProgress': 'orange',
            'Completed': 'green',
            'Cancelled': 'red',
        };
        return colors[status] || 'default';
    };

    return useMemo(() => [
        {
            id: 'sr',
            name: '#',
            selector: 'sr',
            width: 50,
            cell: (row, index) => {
                const currentPage = parseInt(params?.page) || 1;
                const calculatedIndex = (currentPage - 1) * pageSize + index + 1;
                return <span style={{ fontWeight: '500', color: '#666' }}>{calculatedIndex}</span>;
            },
        },
        {
            id: 'orderUniqueId',
            name: 'Order ID',
            selector: 'orderUniqueId',
            width: 160,
            cell: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {record?.isSimulation && (
                        <Tooltip title="Simulation Mode">
                            <img
                                src={PrakurtiCirculerLogo}
                                alt="sim"
                                style={{ width: '16px', height: '16px', borderRadius: '50%' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={`Order: ${record?.orderUniqueId} | Click to view details`}>
                        <Link
                            to={`/order-history-overview/${record?.orderId}`}
                            style={{ fontWeight: '500', color: '#1890ff' }}
                        >
                            {record?.orderUniqueId || '-'}
                        </Link>
                    </Tooltip>
                </div>
            ),
        },
        {
            id: 'customerInfo',
            name: 'Customer',
            selector: 'customerName',
            width: 200,
            cell: (record) => (
                <Tooltip
                    title={
                        <div>
                            <div><strong>Name:</strong> {record?.customerName || '-'}</div>
                            <div><strong>Phone:</strong> {record?.phoneNo || '-'}</div>
                            {record?.parentCustomerName && (
                                <div><strong>Primary:</strong> {record?.parentCustomerName}</div>
                            )}
                        </div>
                    }
                >
                    <div style={{ cursor: 'pointer' }}>
                        <div style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record?.customerName || '-'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record?.phoneNo || '-'}</div>
                        {record?.parentCustomerName && (
                            <div style={{ fontSize: '11px', color: '#1890ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Primary: {record?.parentCustomerName}
                            </div>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            id: 'serviceData',
            name: 'Services',
            selector: row => row.serviceData?.[0]?.name || '-',
            cell: record => (
                <div className="d-flex flex-column gap-1">
                    {record.serviceData?.map((service, index) => (
                        <Tooltip key={index} title={`${service.name} (Qty: ${service.quantity})`}>
                            <Tag color="purple" style={{ marginBottom: '2px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {service.name} {service.quantity > 1 ? `(x${service.quantity})` : ''}
                            </Tag>
                        </Tooltip>
                    ))}
                    {!record.serviceData?.length && <span style={{ color: '#999' }}>-</span>}
                </div>
            ),
            sortable: true,
            width: 180,
        },
        {
            id: 'schedule',
            name: 'Schedule',
            selector: 'date',
            width: 160,
            cell: (record) => {
                const date = record.date ? (dayjs(record.date, 'DD-MM-YYYY').isValid() ? dayjs(record.date, 'DD-MM-YYYY').format('DD MMM YYYY') : record.date) : '-';
                const startTime = formatTime(record.startTime) || '-';
                return (
                    <Tooltip title={`Date: ${date} | Time: ${startTime}`}>
                        <div style={{ cursor: 'pointer' }}>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>{date}</div>
                            <div style={{ marginTop: '2px' }}>
                                <Tag color="cyan" style={{ fontSize: '11px' }}>
                                    {startTime}
                                </Tag>
                            </div>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            id: 'status',
            name: 'Status',
            selector: 'status',
            width: 120,
            cell: (record) => (
                <Tooltip title={`Status: ${record?.status}${record?.isSimulation ? ' (Test Booking)' : ''}`}>
                    <div>
                        <Tag
                            color={getStatusColor(record?.status)}
                            style={{ fontWeight: '500', borderRadius: '10px' }}
                        >
                            {record?.status || '-'}
                        </Tag>
                    </div>
                </Tooltip>
            ),
        },
        {
            id: 'bookedOn',
            name: 'Booked On',
            selector: 'createdAt',
            width: 110,
            cell: (record) => {
                const fullDate = record?.createdAt ? dayjs(record.createdAt).format('DD MMM YYYY, hh:mm A') : '-';
                return (
                    <Tooltip title={`Booked on: ${fullDate}${record?.createdByName ? ` | By: ${record?.createdByName}` : ''}`}>
                        <div style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }}>
                            {record?.createdAt ? dayjs(record.createdAt).format('DD MMM YY') : '-'}
                            {record?.createdByName && (
                                <div style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90px' }}>
                                    by {record?.createdByName}
                                </div>
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
        // {
        //     id: 'actions',
        //     name: 'Actions',
        //     width: 220,
        //     cell: (record) => (
        //         <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        //             {canUpdate && record?.status !== 'Completed' && record?.status !== 'Cancelled' && (
        //                 <Tooltip title="Update booking status">
        //                     <div>
        //                         <InputFromUser
        //                             type="singleSelect"
        //                             name="status"
        //                             onChange={newStatus => onStatusUpdate(record.id || record._id, newStatus)}
        //                             options={['Upcoming', 'Completed']}
        //                             FormGroupClassName={'cursor-pointer'}
        //                             value={record.status}
        //                             label=""
        //                         />
        //                     </div>
        //                 </Tooltip>
        //             )}
        //             <Tooltip title={record?.doctorName ? 'Change assigned doctor' : 'Assign a doctor to this booking'}>
        //                 <Button
        //                     type="primary"
        //                     size="small"
        //                     onClick={() => onAssignDoctor(record?._id)}
        //                     disabled={record.status === 'Completed' || record.status === 'Cancelled'}
        //                 >
        //                     {record?.doctorName ? 'Change' : 'Assign'}
        //                 </Button>
        //             </Tooltip>
        //         </div>
        //     ),
        // },
    ], [canUpdate, onStatusUpdate, onAssignDoctor, params?.page]);
};

